import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EEmailABTestWinningMetric, EMAIL_AB_TEST_VARIATIONS } from '@feathr/blackbox';
import { CardV2 as Card, EmptyState, SegmentedControl } from '@feathr/components';
import {
  EMAIL_AB_TEST_VERSION_COLORS,
  getEmailABTestVersionLabel,
} from '@feathr/extender/utils/emailABTest';

import type { IABTestLine } from './ABTestLineChart';
import ABTestLineChart from './ABTestLineChart';
import type { IABTestReportStats, TABTestPerformancePoint } from './ABTestReport.types';
import { AB_TEST_METRICS, formatABTestRate, getABTestMetricLabel } from './ABTestReport.utils';

import * as styles from './ABTestPerformanceChart.css';

interface IProps {
  performance?: IABTestReportStats['performance'];
}

function ABTestPerformanceChart({ performance }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [metric, setMetric] = useState<EEmailABTestWinningMetric>(EEmailABTestWinningMetric.Opens);

  const lines: Array<IABTestLine<TABTestPerformancePoint>> = EMAIL_AB_TEST_VARIATIONS.map(
    (variation) => ({
      ...EMAIL_AB_TEST_VERSION_COLORS[variation],
      dataKey: variation,
      name: getEmailABTestVersionLabel(variation, t),
    }),
  );

  const metricOptions = AB_TEST_METRICS.map((value) => ({
    label: getABTestMetricLabel(value, t),
    value,
  }));

  function handleMetricChange(value: string): void {
    setMetric(value as EEmailABTestWinningMetric);
  }

  return (
    <Card width={'full'}>
      <Card.Header
        description={t(
          'Comparing performance metrics between versions A and B during the test period',
        )}
        title={t('Performance over time')}
      />
      {performance ? (
        <Card.Content contentClassName={styles.content}>
          <div>
            <SegmentedControl
              data={metricOptions}
              name={'ab-test-performance-metric'}
              onChange={handleMetricChange}
              value={metric}
            />
          </div>
          <ABTestLineChart
            data={performance[metric]}
            lines={lines}
            valueFormatter={formatABTestRate}
          />
        </Card.Content>
      ) : (
        <Card.Content>
          <EmptyState label={t('No data available')} theme={'white'} width={'full'} />
        </Card.Content>
      )}
    </Card>
  );
}

export default ABTestPerformanceChart;
